import { AlertTriangle, Phone, Mail } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useSupabaseHealth } from "@/hooks/useSupabaseHealth";
import { Button } from "@/components/ui/button";

type BannerContext = "gift-card" | "booking" | "contact" | "services" | "generic";

interface SupabaseRestrictionBannerProps {
  /** Force a specific context. If omitted, the message is auto-selected from the route. */
  context?: BannerContext;
}

const getContextFromPath = (pathname: string): BannerContext => {
  const p = pathname.toLowerCase();
  if (p.includes("bon-cadeau") || p.includes("gift")) return "gift-card";
  if (p.includes("contact")) return "contact";
  if (p.includes("prestation") || p.includes("service") || p.includes("soin") || p.includes("carte"))
    return "services";
  if (p.includes("rendez-vous") || p.includes("booking") || p.includes("reserv") || p.includes("annul"))
    return "booking";
  return "generic";
};

const MESSAGES: Record<BannerContext, string> = {
  "gift-card":
    "Les commandes en ligne de bons cadeaux sont temporairement indisponibles. Pour offrir un bon cadeau, contactez-moi directement — je m'occupe de tout personnellement.",
  booking:
    "La réservation en ligne est temporairement indisponible. Pour prendre ou modifier un rendez-vous, contactez-moi directement.",
  contact:
    "Le formulaire de contact est temporairement indisponible. Vous pouvez me joindre directement par téléphone ou par email.",
  services:
    "L'affichage de certaines informations est temporairement perturbé et la réservation en ligne ne fonctionne pas. Pour toute question ou pour réserver un soin, contactez-moi directement.",
  generic:
    "Certains services en ligne sont temporairement indisponibles. Vous pouvez me contacter directement par téléphone ou par email.",
};

const SupabaseRestrictionBanner = ({ context }: SupabaseRestrictionBannerProps) => {
  const { isRestricted } = useSupabaseHealth();
  const location = useLocation();

  if (!isRestricted) return null;

  const resolvedContext = context ?? getContextFromPath(location.pathname);
  const message = MESSAGES[resolvedContext];

  return (
    <div className="sticky top-16 z-20 bg-bbz-cream border-b border-destructive/20 shadow-sm">
      <div role="alert" className="container mx-auto px-4 py-3">
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 md:p-5 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm md:text-base text-bbz-text leading-relaxed">{message}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 md:shrink-0">
            <Button asChild variant="default" size="sm" className="gap-2">
              <a href="tel:+33499538308">
                <Phone className="w-4 h-4" aria-hidden="true" />
                04 99 53 83 08
              </a>
            </Button>
            <Button asChild variant="outline" size="sm" className="gap-2">
              <a href="mailto:contact@biobeautezen.com">
                <Mail className="w-4 h-4" aria-hidden="true" />
                Email
              </a>
            </Button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default SupabaseRestrictionBanner;
